import { Box, Text } from '@bonusly/design-system';

import type { CommentDraftCard } from '../../../schemas/bizy-ui-blocks';
import { AmountPill, HashtagToken } from './lib/recognition-segments';
import { UiBlockActionBar } from './ui-block-action-bar';

/**
 * Comment / add-on draft card.
 *
 * Visual shape — uses the same segment styling as a real recognition
 * (see `src/components/bonus/text-preview.tsx`) so the draft reads
 * like the post the user is about to commit:
 *
 *   [+5] message body ... #hashtag
 *
 *   [📋]                       [Send now] [Insert draft]
 *
 * No giver header, no @recipient — comments are addressed in their
 * parent post's thread context, not pre-tagged. Add-on amount is
 * optional; plain comments omit the pill. No divider line — the
 * action bar's `mt` handles separation.
 */
export function CommentDraftCardView({ block, fontSize = 'sm' }: { block: CommentDraftCard; fontSize?: 'sm' | 'md' }) {
	const { message, amount, hashtags, actions } = block.payload;
	const hashtagList = (hashtags ?? []).map((h) => h.replace(/^#/, ''));

	const copyText = [amount && amount > 0 ? `+${amount}` : null, message, ...hashtagList.map((h) => `#${h}`)].filter(Boolean).join(' ');

	return (
		<Box my={2} px={3} py={3} bg="bg.surface" border="1px solid" borderColor="border.default" borderRadius="md">
			{/* `fontSize="inherit"` on every nested span keeps the body, +amount prefix, and hashtags at the same visual size. */}
			<Text fontSize={fontSize} lineHeight="tall" whiteSpace="pre-wrap">
				{amount != null && amount > 0 && (
					<>
						<AmountPill amount={amount} />{' '}
					</>
				)}
				{message}
				{hashtagList.length > 0 && (
					<>
						{' '}
						{hashtagList.map((h, i) => (
							<span key={`${h}-${i}`}>
								<HashtagToken hashtag={h} />
								{i < hashtagList.length - 1 ? ' ' : ''}
							</span>
						))}
					</>
				)}
			</Text>
			<UiBlockActionBar actions={actions} copyText={copyText} />
		</Box>
	);
}
